import { useId, useMemo } from 'react'
import type { Mission } from '@/content/missions'
import type { MarkStyle } from '@/content/marks'
import Flag from './Flag'
import Wave from './Wave'
import { motifs, rasterize, shapePath } from './motifs'

type AgencyMarkProps = {
  mission: Mission
  variant: MarkStyle
  className?: string
  /** Accessible name; omit when the mark sits beside the agency name. */
  title?: string
}

const publicPath = (file: string) => `/${file.replace(/^public\//, '')}`

function Glyph({ mission, stroke, width }: { mission: Mission; stroke: string; width: number }) {
  return (
    <g fill="none" stroke={stroke} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round">
      {motifs[mission.motif].map((shape, i) => (
        <path key={i} d={shapePath(shape)} />
      ))}
    </g>
  )
}

/** A flat emblem: the motif on a disc of the agency color, ringed in its accent. */
function Emblem({ mission }: { mission: Mission }) {
  return (
    <>
      <circle cx="24" cy="24" r="24" fill="var(--mds-primary)" />
      <circle cx="24" cy="24" r="20.6" fill="none" stroke="var(--mds-accent)" strokeWidth="1.4" />
      <g transform="translate(10.8 10.8) scale(1.1)">
        <Glyph mission={mission} stroke="var(--mds-on-primary)" width={1.7} />
      </g>
    </>
  )
}

/** An original USA logo: the letters in white on a square of flag blue. */
/**
 * The nation's logo: the shield of the Great Seal, thirteen pales of white and
 * red under a blue chief, with USA in the chief. Drawn as shapes, so it needs
 * no typeface and holds its form at any size.
 */
function UsaLogo() {
  const clip = useId()
  const shield = 'M2 2H38V24C38 36 28 42.5 20 46C12 42.5 2 36 2 24Z'
  return (
    <svg viewBox="0 0 40 48" aria-hidden="true">
      <defs>
        <clipPath id={clip}>
          <path d={shield} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clip})`}>
        <rect width="40" height="48" fill="#ffffff" />
        <g fill="#b31942">
          {[1, 3, 5, 7, 9, 11].map((pale) => (
            <rect key={pale} x={2 + pale * (36 / 13)} y="16" width={36 / 13} height="32" />
          ))}
        </g>
        <rect width="40" height="16" fill="#0a3161" />
        <g fill="#ffffff" fillRule="evenodd">
          <path d="M6 4.5H8.6V9.5A1.4 1.4 0 0 0 11.4 9.5V4.5H14V9.5A4 4 0 0 1 6 9.5Z" />
          <path d="M16 4.5H24V6.8H18.3V7.85H24V13.5H16V11.2H21.7V10.15H16Z" />
          <path d="M26 13.5V8.5A4 4 0 0 1 34 8.5V13.5H31.4V11.2H28.6V13.5ZM28.6 9.3H31.4V8.5A1.4 1.4 0 0 0 28.6 8.5Z" />
        </g>
      </g>
      <path d={shield} fill="none" stroke="#0a3161" strokeWidth="2.5" strokeLinejoin="round" />
    </svg>
  )
}

const FLAG_W = 150
const FLAG_H = 100

/**
 * The agency flag as departmental flags are made: the seal centered on a
 * field of the agency color, with an accent stripe at the hoist. Agencies
 * without a seal on file carry their emblem instead.
 */
function AgencyFlagArt({ mission }: { mission: Mission }) {
  const size = 64
  return (
    <>
      <rect width={FLAG_W} height={FLAG_H} fill="var(--mds-primary)" />
      <rect width={6} height={FLAG_H} fill="var(--mds-accent)" />
      {mission.seal ? (
        <image
          href={publicPath(mission.seal.file)}
          x={(FLAG_W - size) / 2 + 3}
          y={(FLAG_H - size) / 2}
          width={size}
          height={size}
        />
      ) : (
        <g transform={`translate(${(FLAG_W - size) / 2 + 3} ${(FLAG_H - size) / 2}) scale(${size / 48})`}>
          <Emblem mission={mission} />
        </g>
      )}
    </>
  )
}

/**
 * A mission's mark in one of seven styles. The nation's marks are the flag.
 * An agency has its flag, seal and logo as published, and original emblems
 * drawn from a motif in its seal for the simple, pixel and line styles.
 */
export default function AgencyMark({ mission, variant, className, title }: AgencyMarkProps) {
  const pixels = useMemo(() => (variant === 'pixel' ? rasterize(motifs[mission.motif], 16) : []), [mission.motif, variant])
  const label = title
  const a11y = label ? { role: 'img' as const, 'aria-label': label } : { 'aria-hidden': true as const }
  const classes = `mds-agency-mark mds-agency-mark--${variant}${className ? ` ${className}` : ''}`

  if (mission.kind === 'nation') {
    if (variant === 'seal' && mission.seal)
      return (
        <span className={classes}>
          <img src={publicPath(mission.seal.file)} alt={label ?? ''} loading="lazy" decoding="async" />
        </span>
      )
    if (variant === 'logo')
      return (
        <span className={classes} {...a11y}>
          <UsaLogo />
        </span>
      )
    return (
      <span className={classes} {...(label ? { role: 'img', 'aria-label': label } : {})}>
        <Flag variant={variant} />
      </span>
    )
  }

  const image = variant === 'logo' ? (mission.logo ?? mission.seal) : variant === 'seal' ? mission.seal : undefined
  if (image)
    return (
      <span className={`${classes}${variant === 'logo' && mission.logo ? ' mds-agency-mark--wide' : ''}`}>
        <img src={publicPath(image.file)} alt={label ?? ''} loading="lazy" decoding="async" />
      </span>
    )

  if (variant === 'flag' || variant === 'motion')
    return (
      <span className={`${classes} mds-agency-mark--wide`} {...a11y}>
        {variant === 'motion' ? (
          <Wave width={FLAG_W} height={FLAG_H}>
            <AgencyFlagArt mission={mission} />
          </Wave>
        ) : (
          <svg viewBox={`0 0 ${FLAG_W} ${FLAG_H}`} aria-hidden="true">
            <AgencyFlagArt mission={mission} />
          </svg>
        )}
      </span>
    )

  if (variant === 'pixel')
    return (
      <span className={classes} {...a11y}>
        <svg viewBox="0 0 20 20" shapeRendering="crispEdges" aria-hidden="true">
          {Array.from({ length: 20 }, (_, y) =>
            Array.from({ length: 20 }, (_, x) => {
              const d = Math.hypot(x + 0.5 - 10, y + 0.5 - 10)
              return d <= 10 ? (
                <rect key={`${x}-${y}`} x={x} y={y} width={1.02} height={1.02} fill={d > 8.7 ? 'var(--mds-accent)' : 'var(--mds-primary)'} />
              ) : null
            }),
          )}
          {pixels.map((row, y) =>
            row.map((on, x) => (on ? <rect key={`p${x}-${y}`} x={x + 2} y={y + 2} width={1.02} height={1.02} fill="var(--mds-on-primary)" /> : null)),
          )}
        </svg>
      </span>
    )

  // simple, and seal or logo when none is on file
  return (
    <span className={classes} {...a11y}>
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <Emblem mission={mission} />
      </svg>
    </span>
  )
}
